import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const API_BASE = process.env.NODE_ENV === 'production' 
  ? 'https://adminconsole.mlwb.in/api' 
  : 'http://localhost:5000/api';

// Helper function to get UTC date string (YYYY-MM-DD)
// Using UTC to match backend and VPS timezone
// Note: Function name kept as getISTDateString for backward compatibility
function getISTDateString() {
  const now = new Date();
  // Use UTC date components directly (matches backend)
  const year = now.getUTCFullYear();
  const month = String(now.getUTCMonth() + 1).padStart(2, '0');
  const day = String(now.getUTCDate()).padStart(2, '0');
  
  return `${year}-${month}-${day}`;
}

export default function RouteTracker() {
  const location = useLocation();

  useEffect(() => {
    // Create a per-tab session id stored in sessionStorage (expires on tab close)
    let sessionId = sessionStorage.getItem('mlwb_session_id');
    if (!sessionId) {
      sessionId = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
      sessionStorage.setItem('mlwb_session_id', sessionId);
    }

    // Ensure unique visit per session per day per page
    // USE UTC TIMEZONE to match backend
    const todayKey = getISTDateString(); // YYYY-MM-DD in UTC
    const pagePath = window.location.pathname;
    const pageKey = `${todayKey}:${sessionId}:${pagePath}`;
    const sentSetKey = 'mlwb_sent_pages';
    const sentPagesRaw = sessionStorage.getItem(sentSetKey);
    const sentPages = sentPagesRaw ? JSON.parse(sentPagesRaw) : {};
    
    // Clean up old entries (older than 2 days) to prevent localStorage bloat
    const twoDaysAgo = new Date();
    twoDaysAgo.setDate(twoDaysAgo.getDate() - 2);
    const twoDaysAgoKey = getISTDateStringForDate(twoDaysAgo);
    Object.keys(sentPages).forEach(key => {
      const keyDate = key.split(':')[0];
      if (keyDate < twoDaysAgoKey) {
        delete sentPages[key];
      }
    });
    
    if (sentPages[pageKey]) {
      return; // already recorded today for this session and page
    }
    sentPages[pageKey] = true;
    sessionStorage.setItem(sentSetKey, JSON.stringify(sentPages));

    const payload = {
      pagePath,
      pageTitle: document.title || '',
      queryParams: window.location.search || '',
      timeOnPage: 0,
      sessionId,
    };

    const url = `${API_BASE}/analytics/track`;
    const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
    const sent = typeof navigator.sendBeacon === 'function' ? navigator.sendBeacon(url, blob) : false;
    if (!sent) {
      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        keepalive: true
      }).catch((error) => {
        // Gracefully handle analytics tracking failures
        console.warn('Analytics tracking failed (non-critical):', error.message || 'Network error');
        // Don't throw - analytics is optional and shouldn't break the user experience
      });
    }
  }, [location]);

  return null;
}

// Helper for cleanup (same logic as getISTDateString but accepts a Date object)
// Using UTC to match backend and VPS timezone
// Note: Function name kept for backward compatibility
function getISTDateStringForDate(date) {
  // Use UTC date components directly (matches backend)
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}


